import { Plus } from 'lucide-react';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import { useIsMobile } from '@/hooks/useIsMobile';

import { CollapsibleCta, CollapsibleSecondary, MobileCollapsible } from '../MobileCollapsible';

export interface CategoryFilterItem {
  id: string;
  label: string;
}

interface CategoryFilterSidebarProps {
  title: string;
  allLabel: string;
  items: CategoryFilterItem[];
  /** Aktywna kategoria; pusty string = „Wszystkie". */
  activeId: string;
  onChange: (id: string) => void;
  addLabel: string;
  onAdd: () => void;
  manageLabel?: string;
  onManage?: () => void;
  /** Dodatkowe linki pod „Zarządzaj..." (np. „Zarządzaj wariantami"). */
  extraLinks?: { label: string; onClick: () => void; icon?: ReactNode }[];
  /** Druga sekcja pod kategoriami (np. „Warianty"): lista linków + „+" i zarządzanie. */
  secondary?: {
    title: string;
    items: CategoryFilterItem[];
    onItemClick: (id: string) => void;
    addLabel: string;
    onAdd: () => void;
    manageLabel?: string;
    onManage?: () => void;
  };
}

/* Stały drugi sidebar (jak w Ustawieniach) z listą kategorii jako filtrem listy,
   okrągłym „+" i linkiem do zarządzania kategoriami. Używany na listach wydarzeń/ofert. */
export const CategoryFilterContent = styled.div`
  min-width: 0;
`;

export const CategoryFilterLayout = styled.div`
  display: grid;
  grid-template-columns: 268px minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  /* Wąski ekran: kolumna w kolejności tytuł strony, zwinięte kategorie, reszta treści.
     Treść ma display: contents, więc jej dzieci układają się obok panelu kategorii. */
  @media (max-width: 1023px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;

    & > :not(${CategoryFilterContent}) {
      order: 1;
      margin-bottom: 4px;
    }

    & > ${CategoryFilterContent} {
      display: contents;

      /* Link powrotu (pierwszy element) i tytuł strony (zawiera h1) idą przed kategoriami. */
      & > * { order: 2; }
      & > :first-child,
      & > :has(h1) { order: 0; }
    }
  }
`;

const Sidebar = styled.nav`
  position: sticky;
  top: 0;
  align-self: start;
  height: calc(100dvh - 65px);
  overflow-y: auto;
  margin-top: -25px;
  margin-left: -48px;
  padding: 32px 16px 24px 36px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  border-right: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};

  @media (max-width: 1023px) {
    position: static;
    height: auto;
    margin: 0;
    padding: 0;
    border-right: none;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
  }
`;

const Title = styled.h2`
  margin: 0 0 8px;
  padding: 0 0 12px;
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};

  @media (max-width: 1023px) {
    width: 100%;
  }
`;

const NavItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: color 0.15s ease;

  &:hover { color: ${({ theme }) => theme.colors.text.secondary}; }

  @media (max-width: 1023px) { width: auto; }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 0 16px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#d1d5db')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:hover { background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6')}; }

  svg { width: 18px; height: 18px; }
`;

const ManageLink = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  text-underline-offset: 3px;

  &:hover { text-decoration: underline; }
`;

const CategoryFilterPanel: FC<CategoryFilterSidebarProps & { hideTitle?: boolean; hideSecondary?: boolean }> = ({
  title, allLabel, items, activeId, onChange, addLabel, onAdd, manageLabel, onManage, extraLinks, secondary, hideTitle,
  hideSecondary,
}) => (
  <Sidebar aria-label={title}>
    {/* W popoverze tytuł jest już na przycisku. */}
    {!hideTitle && <Title>{title}</Title>}
    <NavItem type="button" $active={!activeId} aria-pressed={!activeId} onClick={() => onChange('')}>
      {allLabel}
    </NavItem>
    {items.map((item) => (
      <NavItem
        key={item.id}
        type="button"
        $active={activeId === item.id}
        aria-pressed={activeId === item.id}
        onClick={() => onChange(item.id)}
      >
        {item.label}
      </NavItem>
    ))}
    <Actions>
      {/* W popoverze (wąski ekran) główna akcja jako pigułka CTA na całą szerokość. */}
      {hideTitle ? (
        <CollapsibleCta type="button" onClick={onAdd}>
          <Plus aria-hidden />
          {addLabel}
        </CollapsibleCta>
      ) : (
        <AddButton type="button" onClick={onAdd} aria-label={addLabel} title={addLabel}>
          <Plus aria-hidden />
        </AddButton>
      )}
      {manageLabel && onManage && (hideTitle ? (
        <CollapsibleSecondary type="button" onClick={onManage}>{manageLabel}</CollapsibleSecondary>
      ) : (
        <ManageLink type="button" onClick={onManage}>{manageLabel}</ManageLink>
      ))}
      {extraLinks?.map((link) => (hideTitle ? (
        <CollapsibleSecondary key={link.label} type="button" onClick={link.onClick}>
          {link.icon}
          {link.label}
        </CollapsibleSecondary>
      ) : (
        <ManageLink key={link.label} type="button" onClick={link.onClick} className="inline-flex items-center gap-1.5">
          {link.icon}
          {link.label}
        </ManageLink>
      )))}
    </Actions>
    {secondary && !hideSecondary && (
      <>
        <Title as="h3" className="!mt-8 !text-[20px]">{secondary.title}</Title>
        {secondary.items.map((item) => (
          <NavItem key={item.id} type="button" $active={false} onClick={() => secondary.onItemClick(item.id)}>
            {item.label}
          </NavItem>
        ))}
        <Actions>
          <AddButton type="button" onClick={secondary.onAdd} aria-label={secondary.addLabel} title={secondary.addLabel}>
            <Plus aria-hidden />
          </AddButton>
          {secondary.manageLabel && secondary.onManage && (
            <ManageLink type="button" onClick={secondary.onManage}>{secondary.manageLabel}</ManageLink>
          )}
        </Actions>
      </>
    )}
  </Sidebar>
);

const COLLAPSE_BREAKPOINT = 1024;

/* Druga sekcja (np. „Warianty") jako osobna lista: na wąskim ekranie w osobnym dropdownie. */
const SecondaryFilterPanel: FC<{ secondary: NonNullable<CategoryFilterSidebarProps['secondary']> }> = ({
  secondary,
}) => (
  <Sidebar aria-label={secondary.title}>
    {secondary.items.map((item) => (
      <NavItem key={item.id} type="button" $active={false} onClick={() => secondary.onItemClick(item.id)}>
        {item.label}
      </NavItem>
    ))}
    <Actions>
      <CollapsibleCta type="button" onClick={secondary.onAdd}>
        <Plus aria-hidden />
        {secondary.addLabel}
      </CollapsibleCta>
      {secondary.manageLabel && secondary.onManage && (
        <CollapsibleSecondary type="button" onClick={secondary.onManage}>{secondary.manageLabel}</CollapsibleSecondary>
      )}
    </Actions>
  </Sidebar>
);

/* Dwa dropdowny obok siebie (kategorie i druga sekcja). */
const DropdownRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

/* Wąski ekran: panel zwinięty do przycisku z aktywną kategorią, lista otwiera się w popoverze.
   Druga sekcja (np. warianty) dostaje własny, osobny dropdown obok. */
export const CategoryFilterSidebar: FC<CategoryFilterSidebarProps> = (props) => {
  const {
    title, allLabel, items, activeId, secondary,
  } = props;
  const isNarrow = useIsMobile(COLLAPSE_BREAKPOINT);
  const activeLabel = items.find((item) => item.id === activeId)?.label ?? allLabel;

  if (!isNarrow) return <CategoryFilterPanel {...props} />;

  const categories = (
    <MobileCollapsible label={title} value={activeLabel} breakpoint={COLLAPSE_BREAKPOINT}>
      <CategoryFilterPanel {...props} hideTitle hideSecondary />
    </MobileCollapsible>
  );
  if (!secondary) return categories;

  return (
    <DropdownRow>
      {categories}
      <MobileCollapsible label={secondary.title} value={String(secondary.items.length)} breakpoint={COLLAPSE_BREAKPOINT}>
        <SecondaryFilterPanel secondary={secondary} />
      </MobileCollapsible>
    </DropdownRow>
  );
};
